/**
 * @fileoverview A real-time chat interface component that enables communication with an AI assistant.
 * The component supports user selection, message history, and markdown rendering for AI responses.
 */

import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import './AIChat.css';
import { API_ENDPOINTS } from '../../config/api';
import Markdown from 'markdown-to-jsx';
import axios from 'axios';

/**
 * @typedef {Object} Message
 * @property {'user' | 'assistant'} role - The role of the message sender
 * @property {string} content - The content of the message
 * @property {Date} timestamp - When the message was sent
 */

/**
 * @typedef {Object} User
 * @property {string} client_id - Unique identifier for the client
 * @property {string} [name] - Optional display name for the client
 */

/**
 * ChatInterface component provides a real-time messaging interface with AI.
 * Features include:
 * - User selection dropdown
 * - Real-time message history
 * - Markdown rendering for AI responses
 * - Loading states and error handling
 * - Auto-scrolling to latest messages
 * 
 * @returns {React.ReactElement} The rendered chat interface
 */
const ChatInterface = () => {
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [users, setUsers] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [selectedUser, setSelectedUser] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [showDropdown, setShowDropdown] = useState(false);
    const [isLoadingUsers, setIsLoadingUsers] = useState(false);
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);
    const [permissionError, setPermissionError] = useState(null);
    const [isAdmin, setIsAdmin] = useState(false);
    const messagesEndRef = useRef(null);
    const chatMessagesRef = useRef(null);
    const searchInputRef = useRef(null);
    const { getAccessToken } = useAuth();

    // Helper function to get all available options
    const getAllOptions = () => {
        const options = [];
        
        // Add clients
        users.forEach(user => {
            options.push({
                value: user.client_id,
                label: user.name || user.client_id,
                type: 'client',
                group: 'Clients'
            });
        });
        
        // Add employees (if admin)
        if (isAdmin) {
            employees.forEach(employee => {
                options.push({
                    value: `employee:${employee.employee_id}`,
                    label: employee.name,
                    type: 'employee',
                    group: 'Employees'
                });
            });
        }
        
        return options;
    };

    // Filter options based on search query
    const getFilteredOptions = () => {
        // Only show results if search query has 3+ characters
        if (!searchQuery.trim() || searchQuery.trim().length < 3) {
            return [];
        }
        
        const query = searchQuery.toLowerCase();
        return getAllOptions().filter(option => 
            option.label.toLowerCase().includes(query)
        );
    };

    // Get selected user details
    const getSelectedUserDetails = () => {
        if (!selectedUser) return null;
        
        const allOptions = getAllOptions();
        return allOptions.find(option => option.value === selectedUser);
    };

    // Handle option selection
    const handleSelectOption = (option) => {
        setSelectedUser(option.value);
        setSearchQuery('');
        setShowDropdown(false);
    };

    // Handle removing selection
    const handleRemoveSelection = () => {
        setSelectedUser('');
        setSearchQuery('');
    };

    // Handle search input focus
    const handleSearchFocus = () => {
        setShowDropdown(true);
    };

    // Handle search input change
    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
        setShowDropdown(true);
    };

    // Handle clicking outside to close dropdown
    const handleClickOutside = (e) => {
        if (searchInputRef.current && !searchInputRef.current.contains(e.target)) {
            setShowDropdown(false);
        }
    };

    // Add click outside listener
    React.useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Fetch users on component mount
    useEffect(() => {
        const fetchUsers = async () => {
            try {
                setIsLoadingUsers(true);
                setPermissionError(null);
                
                const token = await getAccessToken();
                
                // Fetch regular users/clients
                const response = await axios.get(API_ENDPOINTS.DESKTOP_UPLOAD.USERS);
                
                if (response.data.status === 'success') {
                    setUsers(response.data.users);
                    if (response.data.users.length === 0) {
                        setPermissionError("You don't have access to any users. Please contact an administrator.");
                    }
                }
                
                // Try to fetch employees (only works for admin users)
                try {
                    const employeeResponse = await axios.get(API_ENDPOINTS.CHAT.EMPLOYEES, {
                        headers: {
                            'Authorization': `Bearer ${token}`
                        }
                    });
                    
                    if (employeeResponse.data.status === 'success') {
                        setEmployees(employeeResponse.data.employees);
                        setIsAdmin(true); // If we can fetch employees, user is admin
                    }
                } catch (employeeError) {
                    // Not an admin user or endpoint error - that's fine
                    if (employeeError.response?.status === 403) {
                        setIsAdmin(false);
                        setEmployees([]);
                    } else {
                        console.warn('Error fetching employees:', employeeError);
                        setEmployees([]);
                    }
                }
                
            } catch (error) {
                console.error('Error fetching users:', error);
                if (error.response?.status === 403) {
                    setPermissionError("You don't have permission to access this feature.");
                }
            } finally {
                setIsLoadingUsers(false);
            }
        };
        fetchUsers();
    }, [getAccessToken]);

    // Function to scroll to bottom of messages
    const scrollToBottom = () => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    };

    // Fetch chat history when user is selected
    useEffect(() => {
        const fetchChatHistory = async () => {
            if (!selectedUser) return;
            
            try {
                setIsLoadingHistory(true);
                const token = await getAccessToken();
                const response = await fetch(API_ENDPOINTS.CHAT.HISTORY(selectedUser), {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });
                
                const data = await response.json();
                if (data.status === 'success' && Array.isArray(data.messages)) {
                    // Convert timestamps to Date objects
                    const formattedMessages = data.messages.map(msg => ({
                        ...msg,
                        timestamp: new Date(msg.timestamp)
                    }));
                    setMessages(formattedMessages);
                    // Scroll to bottom after messages are loaded
                    setTimeout(scrollToBottom, 100); // Small delay to ensure messages are rendered
                }
            } catch (error) {
                console.error('Error fetching chat history:', error);
                setMessages([]);
            } finally {
                setIsLoadingHistory(false);
            }
        };

        fetchChatHistory();
    }, [selectedUser, getAccessToken]);

    // Scroll to bottom when new messages are added
    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    useEffect(() => {
        // Remove recent-message class from previous message
        const prevMessage = document.querySelector('.recent-message');
        if (prevMessage) {
            prevMessage.classList.remove('recent-message');
            // Reset any absolute positioning
            prevMessage.style.position = 'relative';
            prevMessage.style.top = 'auto';
            prevMessage.style.left = 'auto';
            prevMessage.style.transform = 'none';
        }

        // Add recent-message class to new message
        if (messagesEndRef.current) {
            const lastMessage = messagesEndRef.current.previousElementSibling;
            if (lastMessage && lastMessage.classList.contains('message')) {
                lastMessage.classList.add('recent-message');
            }
        }
    }, [messages]);

    const handleSend = async () => {
        if (!input.trim() || !selectedUser) return;

        const userMessage = {
            role: 'user',
            content: input,
            timestamp: new Date()
        };

        setMessages(prev => [...prev, userMessage]);
        setInput('');
        setLoading(true);

        try {
            const token = await getAccessToken();
            
            // Determine if this is an employee or client selection
            let requestBody;
            if (selectedUser.startsWith('employee:')) {
                // Employee analytics mode
                const employeeId = selectedUser.replace('employee:', '');
                requestBody = {
                    messages: [...messages, userMessage],
                    employee_id: employeeId,
                    mode: 'employee_analytics'
                };
            } else {
                // Regular client mode
                requestBody = {
                    messages: [...messages, userMessage],
                    client_id: selectedUser
                };
            }
            
            const response = await fetch(API_ENDPOINTS.CHAT.SEND, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(requestBody)
            });

            const data = await response.json();
            
            // Create assistant message with command execution status
            const assistantMessage = {
                role: 'assistant',
                content: data.message,
                timestamp: new Date(data.timestamp),
                isCommand: data.command_executed || false
            };
            
            setMessages(prev => [...prev, assistantMessage]);
        } catch (error) {
            console.error('Error:', error);
            setMessages(prev => [...prev, {
                role: 'assistant',
                content: 'Sorry, I encountered an error. Please try again.',
                timestamp: new Date(),
                isCommand: false
            }]);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="chat-page-wrapper">
            <div className="chat-container">
                <div className="chat-input">
                    <div className="chat-input-wrapper">
                        {/* User Search Interface */}
                        <div className="ai-chat-search-container" ref={searchInputRef}>
                            {/* Search Input */}
                            <div className="ai-chat-search-input-wrapper">
                                <input
                                    type="text"
                                    className="ai-chat-search-input"
                                    placeholder={selectedUser ? "Change selection..." : (isAdmin ? "Search clients or employees..." : "Search clients...")}
                                    value={searchQuery}
                                    onChange={handleSearchChange}
                                    onFocus={handleSearchFocus}
                            disabled={isLoadingUsers}
                                />
                                <div className="ai-chat-search-icon">🔍</div>
                            </div>

                            {/* Selected User Tag */}
                            {selectedUser && (
                                <div className={`ai-chat-search-selected-tag ${getSelectedUserDetails()?.type === 'employee' ? 'employee' : 'client'}`}>
                                    <span className="ai-chat-search-tag-icon">
                                        {getSelectedUserDetails()?.type === 'employee' ? '👥' : '📋'}
                                    </span>
                                    <span className="ai-chat-search-tag-label">
                                        {getSelectedUserDetails()?.label}
                                    </span>
                                    <button 
                                        className="ai-chat-search-tag-remove"
                                        onClick={handleRemoveSelection}
                                        type="button"
                                    >
                                        ×
                                    </button>
                                </div>
                            )}

                            {/* Dropdown Results */}
                            {showDropdown && (
                                <div className="ai-chat-search-dropdown">
                                    {isLoadingUsers ? (
                                        <div className="ai-chat-search-dropdown-loading">Loading...</div>
                                    ) : (
                                        <>
                                            {searchQuery.trim().length < 3 ? (
                                                <div className="ai-chat-search-dropdown-empty">
                                                    Type at least 3 characters to search
                                                </div>
                                            ) : getFilteredOptions().length > 0 ? (
                                                <div className="ai-chat-search-dropdown-options">
                                                    {/* Group options by type */}
                                                    {['Clients', 'Employees'].map(groupName => {
                                                        const groupOptions = getFilteredOptions().filter(option => option.group === groupName);
                                                        if (groupOptions.length === 0) return null;
                                                        
                                                        return (
                                                            <div key={groupName} className="ai-chat-search-option-group">
                                                                <div className="ai-chat-search-group-header">
                                                                    {groupName === 'Clients' ? '📋' : '👥'} {groupName}
                                                                    {groupName === 'Employees' && ' (Analytics)'}
                                                                </div>
                                                                {groupOptions.map(option => (
                                                                    <div
                                                                        key={option.value}
                                                                        className={`ai-chat-search-dropdown-option ${selectedUser === option.value ? 'ai-chat-search-selected' : ''}`}
                                                                        onClick={() => handleSelectOption(option)}
                                                                    >
                                                                        <span className="ai-chat-search-option-icon">
                                                                            {option.type === 'employee' ? '👥' : '📋'}
                                                                        </span>
                                                                        <span className="ai-chat-search-option-label">{option.label}</span>
                                                                        {selectedUser === option.value && (
                                                                            <span className="ai-chat-search-option-check">✓</span>
                                                                        )}
                                                                    </div>
                            ))}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            ) : (
                                                <div className="ai-chat-search-dropdown-empty">
                                                    No results for "{searchQuery}"
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            )}
                        </div>

                        {permissionError && (
                            <div className="permission-error-message">
                                {permissionError}
                            </div>
                        )}

                        <textarea
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
                            placeholder={selectedUser ? 
                                (selectedUser.startsWith('employee:') ? 
                                    "Ask analytics questions like 'How much time has this employee spent on content this week?' or 'What clients has he worked on?'" :
                                    "Ask questions or use commands like 'add to notes saying...' or 'update splits for...' to automate tasks") : 
                                "Please select a client or employee first"
                            }
                            disabled={!selectedUser || isLoadingHistory}
                        />
                    </div>
                </div>
                <div className="chat-messages">
                    <div className="messages-viewport">
                        <div className="messages-content">
                            {isLoadingHistory ? (
                                <div className="message assistant">
                                    <div className="typing-indicator">
                                        <span></span>
                                        <span></span>
                                        <span></span>
                                    </div>
                                </div>
                            ) : (
                                messages.map((message, index) => (
                                    <div 
                                        key={index} 
                                        className={`message ${message.role === 'user' ? 'user' : 'assistant'} ${message.isCommand ? 'command-response' : ''}`}
                                    >
                                        <div className="message-content">
                                            {message.isCommand && (
                                                <div className="command-indicator">
                                                    <span className="command-icon">⚡</span>
                                                    <span className="command-label">Command Executed</span>
                                                </div>
                                            )}
                                            {message.role === 'user' ? (
                                                message.content
                                            ) : (
                                                <Markdown options={{
                                                    forceBlock: true,
                                                    overrides: {
                                                        h1: { props: { className: 'message-heading-1' } },
                                                        h2: { props: { className: 'message-heading-2' } },
                                                        h3: { props: { className: 'message-heading-3' } },
                                                        p: { props: { className: 'message-paragraph' } },
                                                        ul: { props: { className: 'message-list' } },
                                                        li: { props: { className: 'message-list-item' } },
                                                        code: { props: { className: 'message-code' } },
                                                    },
                                                }}>
                                                    {message.content}
                                                </Markdown>
                                            )}
                                        </div>
                                    </div>
                                ))
                            )}
                            {loading && (
                                <div className="message assistant recent-message">
                                    <div className="typing-indicator">
                                        <span></span>
                                        <span></span>
                                        <span></span>
                                    </div>
                                </div>
                            )}
                            <div ref={messagesEndRef} style={{ height: '1px' }} />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ChatInterface;
